import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import { hydrateCollaboratorUsers } from '@/utils/collaborators';
import { CoreEnums } from '@/utils/domain/constants';
import { isJsonLike, toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';

const api = bindServices('entity');

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const relations = {
  data: [],
  pagination: meta,
  searchCriteria: null,
  isActive: '',
  orderBy: '',
};

const initialState = {
  data: [],
  relations,
  orderBy: '',
  pagination: meta,
  newEntity: false,
  currentEntity: null,
  currentField: null,
  activeTab: 'configuration',
  view: 'add',
  deleted: false,
  searchCriteria: null,
  crud: false,
  tokenCrud: false,
  isActive: '',
  entityTracesPagination: meta,
  tracesOrderBy: 'created desc',
  tasksOrderBy: 'created desc',
  generatedToken: null,
  filtersConfig: [],
};

const Model = {
  namespace: 'entity',
  state: initialState,
  effects: {
    *fetchFields({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: `dynamicEntity/config/version/${payload.id}/${payload.versionId}`,
          readPriority: payload.readPriority,
          ..._.pick(payload, ['limit', 'page', 'filter']),
          params: { token: payload.token },
        });

        yield put({
          type: payload.type,
          payload: res?.configurations?.fields || [],
        });
      } catch (e) {
        yield put({ type: payload.type, payload: [] });
      }
    },

    *fetchTokens({ payload }, { call, put, select }) {
      try {
        const token = yield select((o) => o.entity.generatedToken?.token);
        const res = yield call(api.getAll, {
          route: `dynamicEntity/config/tokens/${payload.entityId}`,
          ...payload,
          params: { token },
        });

        yield put({
          type: 'queryTabData',
          payload: { source: 'entityTokens', ...res },
        });
      } catch (e) {
        yield put({
          type: 'queryTabData',
          payload: { source: 'entityTokens', items: [] },
        });
      }
    },

    *fetchLists({ payload }, { call, put, select }) {
      try {
        const token = yield select((o) => o.entity.generatedToken?.token);
        const res = yield call(api.getAll, {
          route: `dynamicEntity/config/lists/${payload.entityId}`,
          ...payload,
          params: {
            token,
            select:
              'id,name,description,order,type,isPublic,isActive,preFiltersLogicalOperator,permissionsFiltersLogicalOperator,formMode,dataOrigin,customListGroupId',
            expand: { customListGroup: { select: 'id,name' } },
          },
        });

        yield put({
          type: 'queryTabData',
          payload: { source: 'entityLists', ...res },
        });
      } catch (e) {
        yield put({
          type: 'queryTabData',
          payload: { source: 'entityLists', items: [] },
        });
      }
    },

    *fetchContributors({ payload }, { call, put, select }) {
      try {
        const token = yield select((o) => o.entity.generatedToken?.token);
        const res = yield call(api.getAll, {
          route: `dynamicEntity/config/collaborators/${payload.entityId}`,
          ...payload,
          ...CoreQueries.DynamicEntity.Contributors,
          params: { token },
        });

        // Defensive hydration: if any row arrives without user-display
        // fields (e.g. because the `$expand=collaboratorUser` was stripped
        // by an OData proxy), batch-fetch the missing users so the drawer
        // can always render a name + avatar.
        const items = Array.isArray(res?.items) ? res.items : [];
        const hydrated = yield call(hydrateCollaboratorUsers, items);

        yield put({
          type: 'queryTabData',
          payload: { source: 'entityContributors', ...res, items: hydrated },
        });
      } catch (e) {
        yield put({
          type: 'queryTabData',
          payload: { source: 'entityContributors', items: [] },
        });
      }
    },

    *fetchDependencies({ payload }, { call, put, select }) {
      try {
        const token = yield select((o) => o.entity.generatedToken?.token);
        const res = yield call(api.getAll, {
          route: `fieldReference/entity/dependencies/${payload.entityId}`,
          ...payload,
          ...CoreQueries.DynamicEntity.Dependencies,
          params: { token },
        });

        yield put({
          type: 'queryTabData',
          payload: { source: 'entityDependencies', ...res },
        });
      } catch (e) {
        yield put({
          type: 'queryTabData',
          payload: { source: 'entityDependencies', items: [] },
        });
      }
    },

    *fetchDependents({ payload }, { call, put, select }) {
      try {
        const token = yield select((o) => o.entity.generatedToken?.token);
        const res = yield call(api.getAll, {
          route: `fieldReference/entity/dependents/${payload.entityId}`,
          ...payload,
          ...CoreQueries.DynamicEntity.Dependents,
          params: { token },
        });

        yield put({
          type: 'queryTabData',
          payload: { source: 'entityDependents', ...res },
        });
      } catch (e) {
        yield put({
          type: 'queryTabData',
          payload: { source: 'entityDependents', items: [] },
        });
      }
    },

    *fetchTraces({ payload }, { call, put, select }) {
      try {
        const token = yield select((o) => o.entity.generatedToken?.token);
        const res = yield call(api.getAll, {
          route: `dynamicEntity/config/traces/${payload.id}`,
          ...payload,
          params: { token },
        });

        yield put({
          type: 'queryTraces',
          payload: { ...res, ...payload },
        });
      } catch (e) {
        yield put({
          type: 'queryTabData',
          payload: { source: 'entityTraces', items: [] },
        });
      }
    },

    *fetchTasks({ payload }, { call, put, select }) {
      try {
        const token = yield select((o) => o.entity.generatedToken?.token);
        const res = yield call(api.getAll, {
          route: `entity/tasks/${payload.id}`,
          ...payload,
          params: { token },
        });

        yield put({
          type: 'queryTasks',
          payload: { ...res, ...payload },
        });
      } catch (e) {
        yield put({
          type: 'queryTabData',
          payload: { source: 'entityTasks', items: [] },
        });
      }
    },

    *fetchEntity({ payload }, { call, put }) {
      try {
        const res = yield call(api.getById, { route: 'dynamicEntity', ...payload });
        yield put({ type: 'queryEntity', payload: res });
      } catch (e) {
        yield put({ type: 'onDelete' });
      }
    },

    *generateToken({ payload }, { call, put }) {
      try {
        let res;
        let generatedToken = localStorage.getItem(`x-${window.tabID}-entity-token`);
        generatedToken = isJsonLike(generatedToken) ? JSON.parse(generatedToken) : null;

        if (generatedToken?.token && !payload.isModeChanged) {
          res = generatedToken;
        } else {
          const { id, mode, versionId } = payload;
          const ids = [id, mode, versionId].filter(Boolean).join('/');

          res = yield call(api.apiRoute, {
            route: `dynamicEntity/config/generateToken/${ids}`,
            method: 'post',
          });

          localStorage.setItem(`x-${window.tabID}-entity-token`, JSON.stringify(res));
        }

        yield put({ type: 'queryAccessMode', payload: res });
      } catch (e) {}
    },

    *editEntity({ payload }, { call, put, select }) {
      try {
        const mode = yield select((o) => o.entity.generatedToken?.mode);

        if (mode === CoreEnums.AccessMode.Write) {
          yield call(api.update, { route: 'dynamicEntity', data: payload });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }

        yield put({ type: 'fetchEntity', payload: { id: payload.id } });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
    },

    *entityField({ payload }, { call, put, select }) {
      try {
        const { entityId, versionId, token, mode } = yield select((o) => o.entity.generatedToken);

        if (mode === CoreEnums.AccessMode.Write) {
          const action = payload.itemId ? 'modify' : 'append';
          const route = `dynamicEntity/config/${action}/${entityId}/${versionId}`;

          yield call(payload.itemId ? api.update : api.create, {
            route,
            data: payload,
            params: { token },
            method: 'post',
          });

          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }

        yield put({ type: 'queryCrudAction' });
        return true;
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
        return false;
      }
    },

    *entityToken({ payload }, { call, put, select }) {
      try {
        const { entityId, token, mode } = yield select((o) => o.entity.generatedToken);

        if (mode === CoreEnums.AccessMode.Write) {
          yield call(api.create, {
            route: `dynamicEntity/config/tokens/${entityId}`,
            params: { token },
            data: payload,
          });

          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }

        yield put({ type: 'queryTokenCrud' });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
    },

    // Collaborators add — mirrors the lists/apps/aiagent contract: every
    // mutation triggers a single inline GET against the primary replica
    // (`readPriority: true`) so the drawer and the row-command kebab
    // see the row we just POSTed/DELETEd without waiting for the read
    // replica to catch up.
    *entityCollaborator({ payload }, { call, put, select }) {
      const { entityId, token, mode } = yield select((o) => o.entity.generatedToken);
      try {
        if (mode === CoreEnums.AccessMode.Write && entityId) {
          yield call(api.create, {
            route: `dynamicEntity/config/collaborators/${entityId}`,
            params: { token },
            data: payload,
          });

          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {}
      if (entityId) {
        yield put({
          type: 'fetchContributors',
          payload: { entityId, readPriority: true },
        });
      }
    },

    *deleteField({ payload }, { call, put, select }) {
      try {
        const { entityId, versionId, token, mode } = yield select((o) => o.entity.generatedToken);
        if (mode === CoreEnums.AccessMode.Write) {
          yield call(api.update, {
            route: `dynamicEntity/config/remove/${entityId}/${versionId}`,
            data: { elementType: 'form-field', itemId: payload.id },
            params: { token },
            method: 'post',
          });

          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }

        yield put({ type: 'queryCrudAction' });
      } catch (e) {}
    },

    *deleteToken({ payload }, { call, put, select }) {
      try {
        const { entityId, token, mode } = yield select((o) => o.entity.generatedToken);
        if (mode === CoreEnums.AccessMode.Write) {
          yield call(api.apiRoute, {
            route: `dynamicEntity/config/tokens/${entityId}/${payload.id}`,
            method: 'delete',
            params: { token },
          });

          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }

        yield put({ type: 'queryTokenCrud' });
      } catch (e) {}
    },

    *fetchEntityListDetail({ payload }, { call }) {
      try {
        const res = yield call(api.getById, {
          route: 'customList',
          id: payload.id,
          params: {
            select:
              'id,name,description,order,type,isPublic,isActive,preFiltersLogicalOperator,permissionsFiltersLogicalOperator,formMode,dataOrigin,customListGroupId',
            expand: { customListGroup: { select: 'id,name' } },
          },
        });
        return res;
      } catch (e) {
        return null;
      }
    },

    *createEntityList({ payload }, { call, put }) {
      try {
        const res = yield call(api.create, {
          route: 'customList',
          data: payload,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'queryCrudAction' });
        return res;
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
        return null;
      }
    },

    *updateEntityList({ payload }, { call, put }) {
      try {
        const res = yield call(api.update, {
          route: 'customList',
          data: payload,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'queryCrudAction' });
        return res || true;
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
        return null;
      }
    },

    *deleteEntityList({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `customList/${payload.id}`,
          method: 'delete',
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'queryCrudAction' });
      } catch (e) {}
    },

    // Collaborators delete — same inline refresh contract as
    // `entityCollaborator` (add). See the comment there.
    *removeCollaborator({ payload }, { call, put, select }) {
      const { entityId, token, mode } = yield select((o) => o.entity.generatedToken);
      try {
        if (mode === CoreEnums.AccessMode.Write && entityId && payload?.id) {
          yield call(api.apiRoute, {
            route: `dynamicEntity/config/collaborators/${entityId}/${payload.id}`,
            method: 'delete',
            params: { token },
          });

          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {}
      if (entityId) {
        yield put({
          type: 'fetchContributors',
          payload: { entityId, readPriority: true },
        });
      }
    },

    *deleteEntityField({ payload }, { call, put, select }) {
      try {
        const mode = yield select((o) => o.entity.generatedToken?.mode);

        if (mode === CoreEnums.AccessMode.Write) {
          yield call(api.apiRoute, { route: `dynamicEntityField/${payload.id}`, method: 'delete' });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }

        yield put({ type: 'queryCrudAction' });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error' },
          });
        }
      }
    },

    *publishEntity({}, { call, put, select }) {
      try {
        const { entityId, versionId, token, mode } = yield select((o) => o.entity.generatedToken);
        if (mode === CoreEnums.AccessMode.Write) {
          const res = yield call(api.apiRoute, {
            route: `dynamicEntity/config/publish/${entityId}/${versionId}`,
            params: { token },
            method: 'post',
          });

          if (res?.message) {
            yield put({
              type: 'global/showNotificationsGlobal',
              payload: { visible: true, type: 'success', message: res.message },
            });
          }

          if (res?.newToken) {
            localStorage.setItem(`x-${window.tabID}-entity-token`, JSON.stringify(res.newToken));
            yield put({ type: 'queryAccessMode', payload: res.newToken });
          }
        }
      } catch (e) {
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: e?.message,
          },
        });
      }
    },

    *purge({ payload }, { call, put }) {
      try {
        const route = payload.name
          ? `entity/purge/${payload.id}/${encodeURIComponent(payload.name)}`
          : `entity/purge/${payload.id}`;
        const res = yield call(api.apiRoute, {
          route,
          method: 'post',
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message:
              res?.message ?? getIntl().formatMessage({ id: 'entity.notification.purge.success' }),
          },
        });

        yield put({ type: 'queryPurge' });
      } catch (e) {}
    },

    *setActiveTab({ payload }, { put }) {
      yield put({ type: 'updateTab', payload });
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *showAddForm({ payload }, { put }) {
      yield put({ type: 'addForm', payload });
    },

    *hideAllForms(o, { put }) {
      yield put({ type: 'hideForms' });
    },

    *setOrderBy({ payload }, { put }) {
      const { type, value } = payload;
      yield put({ type, payload: value });
    },

    *isActive({ payload }, { put }) {
      const { type, value } = payload;
      yield put({ type, payload: value });
    },

    *onDelete(o, { put }) {
      yield put({ type: 'entityDeleted' });
    },
  },

  reducers: {
    queryFields(state, { payload }) {
      return {
        ...state,
        data: payload,
        crud: false,
        readPriority: false,
      };
    },

    queryRelations(state, { payload }) {
      return {
        ...state,
        crud: false,
        relations: {
          ...state.relations,
          data: payload.items,
          pagination: {
            ...state.pagination,
            ...payload,
            ...toPaginate(payload),
          },
        },
      };
    },

    queryEntity(state, { payload }) {
      return {
        ...state,
        currentEntity: payload,
        purge: false,
      };
    },

    queryTabData(state, { payload }) {
      return {
        ...state,
        crud: false,
        readPriority: false,
        [payload.source]: payload.items,
      };
    },

    queryTraces(state, { payload }) {
      return {
        ...state,
        entityTraces: payload.items,
        entityTracesPagination: {
          ...state.entityTracesPagination,
          ...payload,
          ...toPaginate(payload),
        },
      };
    },

    queryTasks(state, { payload }) {
      return {
        ...state,
        entityTasks: payload.items,
        entityTasksPagination: {
          ...state.entityTasksPagination,
          ...payload,
          ...toPaginate(payload),
        },
      };
    },

    queryAccessMode(state, { payload }) {
      return {
        ...state,
        generatedToken: payload,
      };
    },

    queryEntityField(state, { payload }) {
      return {
        ...state,
        currentField: payload.item,
      };
    },

    queryCrudAction(state) {
      return {
        ...state,
        crud: Date.now(),
        readPriority: true,
      };
    },

    queryTokenCrud(state) {
      return { ...state, tokenCrud: Date.now() };
    },

    queryOrderBy(state, { payload }) {
      return {
        ...state,
        tracesOrderBy: payload || 'created desc',
      };
    },

    deleted(state) {
      return {
        ...state,
        deleted: true,
      };
    },

    fieldsOrderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
        newField: false,
        currentField: null,
      };
    },

    relationsOrderBy(state, { payload }) {
      return {
        ...state,
        relations: {
          ...state.relations,
          orderBy: payload,
        },
        newField: false,
        currentField: null,
      };
    },

    filtersConfig(state, { payload }) {
      return {
        ...state,
        filtersConfig: payload,
      };
    },

    updateTab(state, { payload }) {
      return {
        ...state,
        activeTab: payload,
        newField: false,
        currentField: null,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        [payload.type]: payload.criteria,
      };
    },

    searchFields(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
      };
    },

    searchRelations(state, { payload }) {
      return {
        ...state,
        relations: {
          ...state.relations,
          searchCriteria: payload,
        },
      };
    },

    addForm(state, { payload }) {
      return {
        ...state,
        view: payload.view,
        newField: payload.state,
        currentField: null,
      };
    },

    entityDeleted(state) {
      return { ...state, deleted: true };
    },

    stateFields(state, { payload }) {
      return {
        ...state,
        isActive: payload,
      };
    },

    stateRelations(state, { payload }) {
      return {
        ...state,
        relations: {
          ...state.relations,
          isActive: payload,
        },
      };
    },

    queryPurge(state) {
      return {
        ...initialState,
        crud: true,
        purge: true,
        activeTab: state.activeTab,
        entityTokens: undefined,
        entityLists: undefined,
        entityContributors: undefined,
        entityDependencies: undefined,
        entityDependents: undefined,
        entityTraces: undefined,
      };
    },

    hideForms() {
      return initialState;
    },
  },
};

export default Model;
